/**
 * From the API's shapes to @timefly/core's, and the rule entries a screen shows.
 * Daily rules come from the API (validation_errors, the tenant's agreed
 * hours); the week rules the backend lacks (35 h rest, every other Sunday)
 * and the open punch come from core. Times are read in Europe/Oslo.
 */
import type { ApiUser, Shift, ValidationError } from '@/api/data';
import type { Tenant } from '@/shell/tenant';
import {
  type BreakPolicy,
  type Person,
  type RuleEntry,
  type ShiftLike,
  type TenantRules,
  type WorkedResult,
  addDays,
  checkShift,
  checkWeek,
  effectiveAutoMinutes,
  hoursOf,
  workedHoursAt,
  workedMinutesAt,
} from '@timefly/core';

const fmt = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Oslo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
/** 'YYYY-MM-DD' and 'HH:MM' in Oslo time. */
export function oslo(iso: string | null | undefined): { date: string; time: string } | null {
  if (!iso) return null;
  const s = fmt.format(new Date(iso)); // 'YYYY-MM-DD HH:MM'
  return { date: s.slice(0, 10), time: s.slice(11, 16) };
}
export const today = (): string => oslo(new Date().toISOString())?.date ?? '';

/** Oslo's offset from UTC at an instant, in milliseconds. */
const offsetAt = (t: number): number => {
  const l = oslo(new Date(t).toISOString());
  return l ? Date.parse(`${l.date}T${l.time}:00Z`) - t : 0;
};

/**
 * The instant of an Oslo wall-clock time, as the ISO string the API stores.
 *
 * Two passes, and the second one is the point. The first reads the offset at the wall-clock
 * time interpreted as UTC, which is an hour off on the two mornings a year when the offset
 * changes between that instant and the real one; the second reads it at the candidate instant
 * and corrects. Before this, 01:30 on 29 March 2026 stored 00:30 and 01:30 on 25 October 2026
 * stored 02:30. Swept over every half-past in 2026: the two passes disagree on three of 8 784
 * wall-clock times and all three are on a transition day (2026-09-21).
 *
 * 02:30 on a spring-forward morning is a time that does not exist. It resolves forward, to
 * 03:30, which is the hour the clock actually reaches; the old code resolved it backwards.
 * 02:30 on an autumn morning happens twice and resolves to the second of them, after the clock
 * has gone back.
 */
export function osloISO(date: string, time: string): string {
  const target = Date.parse(`${date}T${time}:00Z`);
  // a field being typed or cleared is no instant: '' rather than a RangeError that took the whole page down
  // (#272 M-01, clearing «Fra» in Ny vakt); callers that save refuse an empty time before they get here
  if (Number.isNaN(target)) return '';
  const once = target - offsetAt(target);
  return new Date(target - offsetAt(once)).toISOString();
}
/** A shift type's 1970-01-01T06:00:00.000Z is the wall-clock 06:00 (the old app reads the UTC hours as local). */
export const typeTime = (iso: string | null | undefined): string => (iso ? iso.slice(11, 16) : '');

export function toShiftLike(s: Shift): ShiftLike {
  const p = oslo(s.start_time);
  const pause =
    s.lunch_in_time && s.lunch_out_time
      ? Math.round((+new Date(s.lunch_out_time) - +new Date(s.lunch_in_time)) / 60000)
      : null;
  // the dated instants the API stores, the same choice the labels make: a punch pair
  // when both exist, the planned pair otherwise; the labels alone lose the date and the
  // offset (TF-REVIEW-FIRST-USE-20260915-02)
  const from = Date.parse((s.punch_in_time && s.punch_out_time ? s.punch_in_time : s.start_time) ?? '');
  const to = Date.parse((s.punch_in_time && s.punch_out_time ? s.punch_out_time : s.end_time) ?? '');
  const spanMin = Number.isFinite(from) && Number.isFinite(to) ? (to - from) / 60000 : null;
  return {
    id: String(s.id),
    personId: s.user_id == null ? null : String(s.user_id),
    date: p?.date ?? '',
    plannedFrom: p?.time ?? null,
    plannedTo: oslo(s.end_time)?.time ?? null,
    punchIn: oslo(s.punch_in_time)?.time ?? null,
    punchOut: oslo(s.punch_out_time)?.time ?? null,
    pauseMin: pause,
    spanMin,
  };
}
/** The person for display and identity. The company's break flag is not a paid break; it lives in breakPolicy. */
export function toPerson(u: ApiUser, tenant: Tenant | undefined): Person {
  return {
    id: String(u.id),
    // withheld is not 37,5: the agreed week reaches administrators only, and
    // inventing one here is what printed a contract nobody agreed to
    // 0 or below is a missing week (#54), not a week of zero hours that flags every hour as over
    norm: u.max_hours_per_week != null && u.max_hours_per_week > 0 ? u.max_hours_per_week : null,
    breakMin: tenant?.lunch_length_minutes ?? 30,
    // AML §10-5 and §10-12, from this company's membership since 2026-09-23. Absent, because the
    // reader may not see the personnel record, is the ordinary rules: a withheld agreement must
    // not silence a limit, which is core's own reading (ceilings).
    avg: u.averaging ?? 'none',
    independent: !!u.exempt_position,
  };
}
/**
 * The person as the rule engine (checkShift, checkWeek) has always seen it:
 * the company flag still reaches core's paidBreak here, so the day and week
 * rules count exactly as before this package. Kept apart from display on
 * purpose; the discrepancy against the report is reported, not decided
 * (TF-TIME-TOTALS report).
 */
export function rulePerson(u: ApiUser, tenant: Tenant | undefined): Person {
  return { ...toPerson(u, tenant), paidBreak: !!tenant?.lunch_included };
}
/**
 * The company's break policy for worked time, as the payroll report applies
 * it. Null until the company has loaded: nothing is displayed as a guess.
 */
export function breakPolicy(tenant: Tenant | undefined): BreakPolicy | null {
  if (!tenant) return null;
  return {
    auto: !!tenant.lunch_included,
    mode: tenant.break_mode,
    autoMinutes: effectiveAutoMinutes(tenant.lunch_length_minutes),
    breakFromHours: 5.5,
  };
}
/** The shift's actual instants as the API stores them; the calculation keeps dates and offsets. */
export const instantsOf = (s: Shift) => ({
  punchIn: s.punch_in_time,
  punchOut: s.punch_out_time,
  breakIn: s.lunch_in_time,
  breakOut: s.lunch_out_time,
  stint: s.stint,
  breakMode: s.break_mode,
  // where ordinary time ends, so the paid §10-9(2) break stays worked time, as payroll counts it (#252, WT-13)
  plannedEnd: s.end_time,
});
export { markStints } from './stints';
/** Full-shift result on the actual instants; 'unavailable' before the policy is known or without both punches, 'invalid' when out is before in. */
export const workedResult = (s: Shift, policy: BreakPolicy | null): WorkedResult =>
  policy ? workedMinutesAt(instantsOf(s), policy) : { status: 'unavailable' };
/**
 * The hours a shift is paid for, for wage cost: the clocked pair when both punches are there, else the plan, less
 * the break by the company's rule as pay applies it (workedMinutesAt). Until 2026-10-28 the screens priced the
 * whole plan, so an unpaid half hour taken off every shift made cost about 6 % high (BK). Without a company: hours().
 */
export const paidHours = (s: Shift, policy: BreakPolicy | null): number => {
  if (!policy) return hours(s, null);
  const pair = !!(s.punch_in_time && s.punch_out_time);
  const r = workedMinutesAt(
    {
      ...instantsOf(s),
      punchIn: pair ? s.punch_in_time : s.start_time,
      punchOut: pair ? s.punch_out_time : s.end_time,
    },
    policy,
  );
  return r.status === 'ok' ? r.minutes / 60 : hours(s, null);
};
/**
 * The hours a planned shift is paid for, for a figure labelled planned (#263): planned start to planned end
 * less the company's automatic break, the way paidHours prices an unclocked plan. Punches, a clocked break,
 * the punched stint and the mode the shift was punched in under never reach it, so a 72 h mis-punch on an
 * 8 h plan is still 8 h, 7,5 after an unpaid half hour. Under the punched rule a plan has no break to take.
 * ponytail: one automatic break per planned shift; back-to-back planned shifts each take their own, where pay
 * takes one per clocked stint. Join planned stints if a split plan shows up in the figures.
 */
export const plannedPaidHours = (s: Shift, policy: BreakPolicy | null): number => {
  if (!policy) return plannedHours(s);
  const r = workedMinutesAt({ punchIn: s.start_time, punchOut: s.end_time }, policy);
  return r.status === 'ok' ? Math.max(0, r.minutes / 60) : plannedHours(s);
};
/** Full-shift hours after breaks on the actual instants; null unless the result is ok. */
export const worked = (s: Shift, policy: BreakPolicy | null): number | null =>
  policy ? workedHoursAt(instantsOf(s), policy) : null;
/** Sum of worked hours over shifts; shifts without a value add nothing. Null before the policy is known. */
export const workedSum = (shifts: Shift[], policy: BreakPolicy | null): number | null =>
  policy ? shifts.reduce((a, s) => a + (worked(s, policy) ?? 0), 0) : null;
/**
 * The company's rules as core reads them. The punch tolerance is the company's own since #294 ONB-09
 * (tenants.punch_tolerance_minutes, 15 unless changed, the same value the server's punchTolerance reads);
 * without the company row it is 15.
 */
export const tenantRules = (t?: Pick<Tenant, 'punch_tolerance_minutes'> | null): TenantRules => ({
  toleranceMin: t?.punch_tolerance_minutes ?? 15,
  breakFromHours: 5.5,
  today: today(),
});

export type Entry = {
  code: string;
  sev: 'w' | 'd';
  args: Record<string, string | number>;
  source: 'api' | 'core';
  text?: string;
  /** Weekly API entries: whether the plan or the punches broke the rule. */
  label?: 'Planned' | 'Actual' | undefined;
  /** The day of the shift a daily entry is about (YYYY-MM-DD), set where a list mixes days. */
  day?: string | undefined;
  /** The Monday of the week a weekly entry is about (retest R07). */
  week?: string | undefined;
};
/** Entries about the person, not one shift: said once in a list, however many shifts repeat them. */
export const PERSON_CODES = new Set(['age_unknown', 'school_unknown']);
/** Minutes as a person reads them: "45 min", "1 t 30 min", "24 t". */
export const duration = (m: number) => {
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h === 0 ? `${m} min` : r === 0 ? `${h} t` : `${h} t ${r} min`;
};
const fromApi = (e: ValidationError): Entry => ({
  code: e.code,
  sev: e.severity === 'danger' ? 'd' : 'w',
  // the weekrest wording asks for `gap`, and the API sends that figure as `hours`
  args: {
    hours: e.hours ?? 0,
    minutes: e.minutes ?? 0,
    // "23 t 57 min" rather than "1437 min": a clock time a day off must read as a day (owner, 2026-09-27)
    ...(e.code.startsWith('punch_') ? { duration: duration(e.minutes ?? 0) } : {}),
    max: e.max ?? 0,
    ...(e.code === 'weekrest' ? { gap: e.hours ?? 0 } : {}),
    // noon UTC, so the day is the same in every time zone
    ...(e.from ? { from: Date.parse(`${e.from}T12:00:00Z`) } : {}),
    // the skills a shift type needs that the person lacks, named (#283)
    ...(e.skills ? { skills: e.skills } : {}),
  },
  label: e.label,
  source: 'api',
  text: e.text,
});
const fromCore = (e: RuleEntry): Entry => ({ code: e.code, sev: e.sev, args: e.args, source: 'core' });

/**
 * Every rule entry the server sent on one shift, daily and weekly, one per code (the punched
 * week's answer wins, as weekOf reads it). Hugin's evaluation of a change compares these before
 * and after (issue #131): the server's verdict, not a guess.
 */
export function apiEntries(s: Shift): Entry[] {
  const by = new Map<string, ValidationError>();
  for (const e of s.validation_errors ?? []) {
    const had = by.get(e.code);
    if (!had || (had.label === 'Planned' && e.label === 'Actual')) by.set(e.code, e);
  }
  return [...by.values()].map(fromApi);
}

/** Daily entries on one shift: the API's daily errors plus core's open-punch check. */
export function dayEntries(s: Shift, person: Person, rules: TenantRules): Entry[] {
  const api = (s.validation_errors ?? []).filter((e) => e.type === 'daily').map(fromApi);
  const open = checkShift(toShiftLike(s), person, null, rules)
    .filter((e) => e.code === 'open')
    .map(fromCore);
  return [...open, ...api];
}

export interface Week {
  hours: number;
  /** The agreed week, null when this reader is not allowed to know it. */
  norm: number | null;
  entries: Entry[];
}
/** One person's week: hours from core, weekly entries from the API (deduped by code) and core's rest and Sunday rules. */
export function weekOf(person: Person, monday: string, shifts: Shift[], rules: TenantRules): Week {
  const likes = shifts.map(toShiftLike);
  const r = checkWeek(person, monday, likes, rules);
  // The server judges the same week twice, once on the plan and once on the punches, and repeats
  // each entry on every shift of the week. One line per code: the punched week wins, because that
  // is what happened; the plan's answer is shown only where there is no punched one.
  const byCode = new Map<string, ValidationError>();
  // only this week's shifts: the window is read from the Sunday before, for a night that runs into
  // Monday, and that Sunday's shift carries the server's verdict on the week before (retest R07)
  const sunday = addDays(monday, 6);
  const inWeek = shifts.filter((s) => {
    const d = oslo(s.start_time)?.date ?? '';
    return d >= monday && d <= sunday;
  });
  for (const s of inWeek)
    for (const e of s.validation_errors ?? [])
      if (e.type === 'weekly') {
        const had = byCode.get(e.code);
        if (!had || (had.label === 'Planned' && e.label === 'Actual')) byCode.set(e.code, e);
      }
  const seen = new Set<string>(byCode.keys());
  const api: Entry[] = [...byCode.values()].map((e) => ({ ...fromApi(e), week: monday }));
  // The server judges the same week and sends `weekrest` since 2026-09-22; core's own entry is
  // there for a plan the server has not validated, so it yields when the API already said it.
  const core = r.entries
    .filter((e) => (e.code === 'weekrest' || e.code === 'sundays') && !seen.has(e.code))
    .map((e) => ({ ...fromCore(e), week: monday }));
  return { hours: r.hours, norm: r.norm, entries: [...api, ...core] };
}

/** Planned or recorded span less a recorded pause, core's hoursOf: the schedule's estimate, not worked time. */
export const hours = (s: Shift, person: Person | null): number => hoursOf(toShiftLike(s), person);
/** Planned hours: planned start to planned end, before any break. Punches never change it (review F06). */
export const plannedHours = (s: Shift): number => {
  const h = (Date.parse(s.end_time ?? '') - Date.parse(s.start_time ?? '')) / 36e5;
  return Number.isFinite(h) && h > 0 ? h : 0;
};
/**
 * The planned hours of a shift that fall on one Oslo date (retest R05): 22:00 to 06:00 is two
 * hours on its first day and six on the next, as the day timeline draws it. Every per-day figure
 * uses this; a whole-shift total says so.
 */
export const plannedHoursOn = (s: Shift, date: string): number => {
  const a = Math.max(Date.parse(s.start_time ?? ''), Date.parse(osloISO(date, '00:00')));
  const b = Math.min(Date.parse(s.end_time ?? ''), Date.parse(osloISO(addDays(date, 1), '00:00')));
  return Number.isFinite(a) && Number.isFinite(b) && b > a ? (b - a) / 36e5 : 0;
};
export const isOpen = (s: Shift, day: string): boolean =>
  !!s.punch_in_time && !s.punch_out_time && (oslo(s.start_time)?.date ?? '') < day;
export const isPunched = (s: Shift): boolean => !!s.punch_in_time && !!s.punch_out_time;
export const worst = (entries: Entry[]): 'd' | 'w' | '' =>
  entries.some((e) => e.sev === 'd') ? 'd' : entries.length ? 'w' : '';

/** The catalogue key for an entry: the old app's timeline.rule_* for API codes, web.rule_* for core's. */
export const ruleKey = (e: Entry): string =>
  e.source === 'api' ? `timeline.rule_${e.code}` : `web.rule_${e.code}`;
/** The sentence for an entry; an API code the catalogue does not know falls back to the API's English text. */
export function ruleText(t: (k: string, v?: Record<string, string | number>) => string, e: Entry): string {
  const k = ruleKey(e);
  let s = k;
  // hours as a Norwegian reads them, "9,9", not "9.87" (retest R07)
  const args = Object.fromEntries(
    Object.entries(e.args ?? {}).map(([n, v]) => [
      n,
      typeof v === 'number' && !Number.isInteger(v) && n !== 'from'
        ? v.toLocaleString('nb-NO', { maximumFractionDigits: 1 })
        : v,
    ]),
  );
  try {
    s = t(k, args);
  } catch {
    // a value the sentence needs is missing (an API older than its catalogue line): the API's own text
  }
  return s === k ? (e.text ?? k) : s;
}
